import React, { useState, useEffect, useContext } from 'react';
import {
  Modal, Table, InputNumber, Button, Typography, Space,
  message, Drawer, Input, List, Avatar, Popconfirm, Divider, Row, Col, Grid
} from 'antd';
import { SwapOutlined, SearchOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { LoginContext } from '../../../../context/loginContext';
import { SucursalContext } from '../../../../context/sucursalContext';

const { Text } = Typography;

const CEditarPedidoCliente = ({ visible, onClose, pedido, onGuardarCambios }) => {
  const [detallesEditados, setDetallesEditados] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [articulos, setArticulos] = useState([]);
  const [indexAReemplazar, setIndexAReemplazar] = useState(null);
  const [modoDrawer, setModoDrawer] = useState('agregar'); // 'agregar' | 'reemplazar'
  const { user } = useContext(LoginContext);
  const { sucursal } = useContext(SucursalContext);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    if (pedido?.detalles) {
      setDetallesEditados(
        pedido.detalles.map(d => ({
          ...d,
          dpe_cantidad: Number(d.dpe_cantidad),
          dpe_total: Number(d.dpe_total),
          precioUnitario: Number(d.dpe_total) / Number(d.dpe_cantidad || 1)
        }))
      );
    }
  }, [pedido]);

  const actualizarDetalle = (index, campo, valor) => {
    const copia = [...detallesEditados];
    copia[index][campo] = valor;

    if (campo === 'dpe_cantidad' || campo === 'precioUnitario') {
      const cantidad = Number(copia[index].dpe_cantidad) || 0;
      const precio = Number(copia[index].precioUnitario) || 0;
      copia[index].dpe_total = cantidad * precio;
    }

    setDetallesEditados(copia);
  };

  const abrirDrawerAgregar = () => {
    setModoDrawer('agregar');
    setIndexAReemplazar(null);
    setDrawerVisible(true);
    buscarArticulos('');
  };

  const abrirReemplazoArticulo = (index) => {
    setModoDrawer('reemplazar');
    setIndexAReemplazar(index);
    setDrawerVisible(true);
    buscarArticulos('');
  };

  const eliminarDetalle = (index) => {
    const copia = [...detallesEditados];
    copia.splice(index, 1);
    setDetallesEditados(copia);
  };

  const buscarArticulos = async (nombre) => {
    try {
      const queryParams = new URLSearchParams({
        aem_nombre: nombre,
        asu_habilitado: true,
        asu_sucursal: sucursal.suc_id,
        page: 1,
        limit: 8
      });

      const res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal/sucursal/?${queryParams.toString()}`);
      const data = await res.json();
      setArticulos(data.data || []);
    } catch (err) {
      console.error('Error al buscar artículos', err);
      message.error('No se pudieron cargar artículos');
    }
  };

  const tieneDescuentoVigente = (asu) => {
    if (!asu?.asu_precio_con_descuento || !asu?.asu_fecha_d_descuento || !asu?.asu_fecha_h_descuento) return false;
    const hoy = new Date();
    const desde = new Date(asu.asu_fecha_d_descuento);
    const hasta = new Date(asu.asu_fecha_h_descuento);
    return hoy >= desde && hoy <= hasta;
  };

  const precioDeItem = (item) => {
    const asu = item?.datos_sucursal;
    if (!asu) return 0;
    return Number(tieneDescuentoVigente(asu) ? asu.asu_precio_con_descuento : asu.asu_precioventa) || 0;
  };

  const agregarArticuloDesdeDrawer = (nuevoArticulo) => {
    const aemId = nuevoArticulo.articulo_empresa.aem_id;
    const precio = precioDeItem(nuevoArticulo);

    const nuevo = {
      dpe_id: undefined,
      dpe_articulo: aemId,
      aem_nombre: nuevoArticulo.articulo_empresa.aem_nombre,
      dpe_cantidad: 1,
      precioUnitario: precio,
      dpe_total: precio
    };

    // Anti-duplicado robusto incluso si se dispara dos veces
    setDetallesEditados(prev => {
      if (prev.some(d => Number(d.dpe_articulo) === Number(aemId))) {
        message.warning('Ese artículo ya está en el pedido');
        return prev;
      }
      return [...prev, nuevo];
    });

    setDrawerVisible(false);
  };

  const reemplazarArticulo = (nuevoArticulo) => {
    const nuevoId = nuevoArticulo.articulo_empresa.aem_id;
    const precio = precioDeItem(nuevoArticulo);

    setDetallesEditados(prev => {
      // Evitar duplicado (permitiendo el propio índice en reemplazo)
      const yaExiste = prev.some((d, i) => Number(d.dpe_articulo) === Number(nuevoId) && i !== indexAReemplazar);
      if (yaExiste) {
        message.warning('Ese artículo ya está en el pedido');
        return prev;
      }

      const copia = [...prev];
      const cant = Number(copia[indexAReemplazar]?.dpe_cantidad || 1);

      copia[indexAReemplazar] = {
        ...copia[indexAReemplazar],
        dpe_articulo: nuevoId,
        aem_nombre: nuevoArticulo.articulo_empresa.aem_nombre,
        precioUnitario: precio,
        dpe_total: precio * cant
      };

      return copia;
    });

    setDrawerVisible(false);
  };

  // Columnas Desktop
  const columnasDesktop = [
    {
      title: 'Artículo',
      dataIndex: 'aem_nombre',
      render: (nombre, _, index) => (
        <Space direction="vertical" size={4}>
          <Text>{nombre}</Text>
          <Button
            size="small"
            icon={<SwapOutlined />}
            onClick={() => abrirReemplazoArticulo(index)}
          >
            Reemplazar
          </Button>
        </Space>
      )
    },
    {
      title: 'Cantidad',
      dataIndex: 'dpe_cantidad',
      width: 140,
      render: (cantidad, _, index) => (
        <InputNumber
          min={1}
          size="small"
          value={cantidad}
          onChange={val => actualizarDetalle(index, 'dpe_cantidad', val)}
        />
      ),
    },
    {
      title: 'Precio Unitario',
      dataIndex: 'precioUnitario',
      width: 160,
      render: (precio) => `$${Number(precio || 0).toFixed(2)}`
    },
    {
      title: 'Total',
      dataIndex: 'dpe_total',
      width: 140,
      render: total => `$${Number(total || 0).toFixed(2)}`
    },
    {
      title: '',
      width: 60,
      render: (_, __, index) => (
        <Popconfirm
          title="¿Eliminar este artículo?"
          onConfirm={() => eliminarDetalle(index)}
          okText="Sí"
          cancelText="No"
        >
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      )
    }
  ];

  // Columnas Mobile (más compactas)
  const columnasMobile = [
    {
      title: 'Artículo',
      dataIndex: 'aem_nombre',
      render: (nombre, record, index) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Text>{nombre}</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text type="secondary">Cant.</Text>
            <InputNumber
              min={1}
              size="small"
              value={record.dpe_cantidad}
              onChange={val => actualizarDetalle(index, 'dpe_cantidad', val)}
            />
            <Button
              size="small"
              icon={<SwapOutlined />}
              onClick={() => abrirReemplazoArticulo(index)}
            >
              Reemplazar
            </Button>
          </div>
        </div>
      )
    },
    {
      title: 'P. Unit.',
      dataIndex: 'precioUnitario',
      width: 110,
      render: (precio) => `$${Number(precio || 0).toFixed(2)}`
    },
    {
      title: 'Total',
      dataIndex: 'dpe_total',
      width: 110,
      render: total => `$${Number(total || 0).toFixed(2)}`
    },
    {
      title: '',
      width: 60,
      render: (_, __, index) => (
        <Popconfirm
          title="¿Eliminar este artículo?"
          onConfirm={() => eliminarDetalle(index)}
          okText="Sí"
          cancelText="No"
        >
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      )
    }
  ];

  const columnas = isMobile ? columnasMobile : columnasDesktop;

  const totalPedido = detallesEditados.reduce((sum, item) => sum + (Number(item.dpe_total) || 0), 0);

  const handleGuardar = async () => {
    if (detallesEditados.length === 0) {
      message.error('Debe haber al menos un artículo en el pedido');
      return;
    }

    const tieneError = detallesEditados.some(
      d => Number(d.dpe_cantidad) <= 0 || Number(d.dpe_total) <= 0 || !d.dpe_articulo
    );

    if (tieneError) {
      message.error('Hay artículos con cantidad o total inválido');
      return;
    }

    const ids = detallesEditados.map(d => Number(d.dpe_articulo));
    const setIds = new Set(ids);
    if (setIds.size !== ids.length) {
      message.error('No se permiten artículos repetidos');
      return;
    }

    try {
      const url = `${process.env.REACT_APP_API_URL}/pedido/${pedido.ped_id}/editar-cliente`;

      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`
        },
        body: JSON.stringify({ detalles: detallesEditados })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al actualizar');

      message.success('Pedido actualizado correctamente');
      onGuardarCambios?.();
      onClose?.();
    } catch (err) {
      console.error(err.message);
      message.error(err.message || 'Error al guardar cambios');
    }
  };

  const rowKey = (row, idx) => `${row.dpe_id ?? 'new'}-${row.dpe_articulo}-${idx}`;

  return (
    <>
      <Modal
        open={visible}
        title={`Editar pedido #${pedido?.ped_id}`}
        onCancel={onClose}
        onOk={handleGuardar}
        okText="Guardar"
        cancelText="Cancelar"
        width={isMobile ? '100%' : 900}
        style={{ top: isMobile ? 8 : 50, padding: 0 }}
        bodyStyle={{ padding: isMobile ? 8 : 24 }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: isMobile ? 8 : 12, gap: isMobile ? 8 : 0 }}
        >
          <Col flex="none">
            <Button type="primary" icon={<PlusOutlined />} onClick={abrirDrawerAgregar}>
              {isMobile ? 'Agregar' : 'Agregar artículo'}
            </Button>
          </Col>
          <Col flex="none">
            <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
              Total: ${totalPedido.toFixed(2)}
            </Text>
          </Col>
        </Row>

        <Table
          dataSource={detallesEditados}
          columns={columnas}
          rowKey={rowKey}
          pagination={false}
          size="small"
          scroll={{ x: isMobile ? 600 : 'max-content' }}
        />
      </Modal>

      <Drawer
        title={modoDrawer === 'agregar' ? 'Agregar artículo' : 'Reemplazar artículo'}
        placement="right"
        width={isMobile ? '100%' : 420}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ padding: isMobile ? 12 : 24 }}
      >
        <Input.Search
          placeholder="Buscar artículo"
          enterButton={<SearchOutlined />}
          onSearch={(val) => buscarArticulos(val)}
          allowClear
          style={{ marginBottom: 12 }}
        />
        <Divider style={{ margin: isMobile ? '8px 0 12px' : '12px 0 16px' }} />
        <List
          itemLayout={isMobile ? 'vertical' : 'horizontal'}
          dataSource={articulos}
          renderItem={(item) => {
            const aem = item.articulo_empresa;
            const precio = precioDeItem(item);

            const onSelect = () => {
              if (modoDrawer === 'agregar') {
                agregarArticuloDesdeDrawer(item);
              } else {
                reemplazarArticulo(item);
              }
            };

            return (
              <List.Item
                /* Importante: sin onClick acá para evitar doble disparo */
                actions={
                  isMobile
                    ? undefined
                    : [
                        <Button
                          type="link"
                          onClick={(e) => { e.stopPropagation(); onSelect(); }}
                          key="action"
                        >
                          {modoDrawer === 'agregar' ? 'Agregar' : 'Reemplazar'}
                        </Button>
                      ]
                }
              >
                <List.Item.Meta
                  avatar={<Avatar src={aem.aem_imagen} />}
                  title={aem.aem_nombre}
                  description={`$${Number(precio).toFixed(2)}`}
                />
                {isMobile && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      block
                      onClick={(e) => { e.stopPropagation(); onSelect(); }}
                    >
                      {modoDrawer === 'agregar' ? 'Agregar' : 'Reemplazar'}
                    </Button>
                  </div>
                )}
              </List.Item>
            );
          }}
        />
      </Drawer>
    </>
  );
};

export default CEditarPedidoCliente;